import DrawerViewTopicalRisk from '@app/src/components/Drawer/Views/DrawerViewTopicalRisk'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import { AnswerFlagArea, RiskStatus, SustainabilityArea } from '@app/src/types/resourceExplorer'
import type { SustainabilityTopicRisk } from '@app/src/types/topicalRisk'
import { sortAlphabetically } from '@app/src/utils/helpers'
import { AmplitudeTrackingEvents } from '@app/src/wf-constants'
import * as React from 'react'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useDrawer } from '../../../components/Drawer/DrawerContext'
import type { RiskAreaItem } from './RiskAreaCard'
import RiskAreaCard from './RiskAreaCard'

// TODO: This can be replaced with a pure SustainabilityTopicRisk when we don't have to mock supply chain risks
export type TopicItem = Pick<SustainabilityTopicRisk, 'totalRiskStatus' | 'providerId'> & {
  sustainabilityTopic: Pick<SustainabilityTopicRisk['sustainabilityTopic'], 'id' | 'name'>
}

interface TopicalProviderRiskAreaCardProps {
  sustainabilityArea: SustainabilityArea
  topicRisks: TopicItem[]
}

export const sustainabilityAreaTranslationKeys: Record<SustainabilityArea | typeof AnswerFlagArea, string> = {
  [SustainabilityArea.Environment]: 'sustainabilityArea.environment',
  [SustainabilityArea.Social]: 'sustainabilityArea.social',
  [SustainabilityArea.Governance]: 'sustainabilityArea.governance',
  [SustainabilityArea.SupplyChain]: 'sustainabilityArea.supplyChain',
  [AnswerFlagArea]: 'sustainabilityArea.answerFlag',
}

const TopicalRiskAreaCard = ({ sustainabilityArea, topicRisks }: TopicalProviderRiskAreaCardProps) => {
  const { formatMessage } = useIntl()
  const { openDrawer } = useDrawer()
  const { trackEvent } = useAmplitude()

  const items: RiskAreaItem[] = useMemo(() => {
    // First sort alphabetically
    const alphabeticallySorted = sortAlphabetically(topicRisks, item => item.sustainabilityTopic.name)

    // Then sort so NotApplicable items appear last
    const sorted = alphabeticallySorted.sort((a, b) => {
      const aIsNotSet = a.totalRiskStatus === RiskStatus.NotApplicable
      const bIsNotSet = b.totalRiskStatus === RiskStatus.NotApplicable

      if (aIsNotSet && !bIsNotSet) return 1
      if (!aIsNotSet && bIsNotSet) return -1
      return 0
    })

    return sorted.map(sustainabilityTopicRisk => ({
      id: sustainabilityTopicRisk.sustainabilityTopic.id,
      title: sustainabilityTopicRisk.sustainabilityTopic.name,
      riskStatus: sustainabilityTopicRisk.totalRiskStatus,
      onClick: () => {
        if (!sustainabilityTopicRisk.sustainabilityTopic.id) return

        trackEvent({
          name: AmplitudeTrackingEvents.Analyze.SupplierRisk.ClickedTopicRisk,
          eventProps: {
            providerId: sustainabilityTopicRisk.providerId,
            sustainabilityArea: sustainabilityArea,
            topic: sustainabilityTopicRisk.sustainabilityTopic.name,
          },
        })
        openDrawer(
          <DrawerViewTopicalRisk
            title={formatMessage(
              { id: 'topicalRisk.drawerHeader' },
              { topic: sustainabilityTopicRisk.sustainabilityTopic.name },
            )}
            sustainabilityTopicRisk={sustainabilityTopicRisk as SustainabilityTopicRisk}
          />,
        )
      },
    }))
  }, [topicRisks, formatMessage, openDrawer])

  return (
    <RiskAreaCard
      area={sustainabilityArea}
      title={formatMessage({ id: sustainabilityAreaTranslationKeys[sustainabilityArea] })}
      items={items}
    />
  )
}

export default TopicalRiskAreaCard
